// Central site data: contact, navigation, services, milestones, trust content.

export const CONTACT = {
  phone: "+351 912 324 121",
  phoneHref: "tel:+351912324121",
  whatsapp: "https://wa.me/351912324121",
  email: "info@effizenteurope.com",
  address:
    "Praça Mouzinho de Albuquerque, no 113, 1º andar, Loja 76, Sala A, 4100-359 Porto",
  addressShort: "Porto, Portugal",
  offices: [
    {
      country: "Portugal",
      city: "Porto",
      address: "Praça Mouzinho de Albuquerque, no 113, 1º andar, Loja 76, Sala A, 4100-359 Porto",
    },
    {
      country: "India",
      city: "New Delhi",
      address: "C-338, Palam Extension, Dwarka, Sec-7, New Delhi, 110075",
    },
  ],
};

export const BRAND = {
  name: "Effizent Europe",
  tagline: "Portugal Business & Relocation Partner",
  promise:
    "We don't just help you move to Portugal. We help you build your future in Portugal.",
};

// Services grouped by pillar. Each is a full page at /services/:slug
export const SERVICES = [
  // BUILD
  {
    slug: "company-formation",
    group: "BUILD",
    icon: "Building2",
    title: "Company Formation",
    tagline: "Your Portuguese company, structured for growth.",
    summary:
      "End-to-end formation of your Portuguese company (Sociedade por Quotas / LDA) — from name reservation to a fully registered business ready to operate.",
    what:
      "Company formation is the legal process of registering your business in Portugal. For most entrepreneurs this means an LDA (a private limited company), the most flexible and widely used structure for foreign founders.",
    why:
      "A properly structured company is the foundation of your visa application, your bank account, your tax residency and your ability to trade across the European Union. Getting it right from day one prevents costly restructuring later.",
    who: "Founders, entrepreneurs and businesses expanding into the EU market.",
    how: [
      "Advisory on the right legal structure (LDA, sole trader, branch)",
      "Company name reservation with the National Registry (RNPC)",
      "Drafting of articles of association and shareholder agreements",
      "Registration with Commercial Registry and Finanças",
      "Share capital setup and beneficial owner declaration (RCBE)",
    ],
    duration: "2–4 weeks",
    cost: "From €1,500 (excl. government fees)",
    faqs: [
      { q: "Can an Indian citizen own 100% of a Portuguese company?", a: "Yes. Non-EU nationals can own 100% of a Portuguese LDA. You do not need a local partner." },
      { q: "Do I need to be in Portugal to form the company?", a: "No. With a power of attorney and your NIF, Effizent Europe can complete the entire incorporation remotely." },
      { q: "What is the minimum share capital?", a: "The legal minimum for an LDA is €1 per shareholder, though we advise a realistic capital that supports your visa and banking goals." },
    ],
    related: ["business-consulting", "nif-registration", "bank-account", "business-plan"],
  },
  {
    slug: "business-consulting",
    group: "BUILD",
    icon: "Briefcase",
    title: "Business Consulting",
    tagline: "Understand the market before you commit.",
    summary:
      "Strategic consulting to validate your idea, understand the Portuguese and European market, and design a realistic path to a profitable business.",
    what:
      "Business consulting is a structured assessment of your opportunity in Portugal — market demand, competition, regulation, costs and the fastest viable route to revenue.",
    why:
      "Portugal is a gateway to 450 million EU consumers, but every sector has its own rules and rhythms. Expert guidance turns ambition into a decision you can defend to a bank, an investor and an immigration officer.",
    who: "First-time founders, established Indian businesses, and investors exploring Europe.",
    how: [
      "Opportunity and market-fit assessment",
      "Competitor and pricing analysis",
      "Regulatory and licensing review for your sector",
      "Go-to-market and location strategy",
      "Investment and cost modelling",
    ],
    duration: "1–3 weeks",
    cost: "From €600",
    faqs: [
      { q: "Is Portugal good for Indian entrepreneurs?", a: "Yes. Portugal offers a stable EU base, competitive costs, a welcoming startup ecosystem and clear entrepreneur visa routes such as the D2." },
      { q: "Which sectors work best?", a: "Technology, services, hospitality, trade/import-export, consulting and food businesses are consistently strong for Indian founders." },
    ],
    related: ["business-plan", "company-formation", "expansion"],
  },
  {
    slug: "business-plan",
    group: "BUILD",
    icon: "FileText",
    title: "Business Plans",
    tagline: "The document that unlocks your visa and your bank.",
    summary:
      "Investor-grade business plans built to satisfy Portuguese immigration (D2), banks and your own strategy — with realistic financials and market research.",
    what:
      "A business plan is a formal document describing your company, market, operations and 3-year financial projections. For the D2 entrepreneur visa it is the single most scrutinised document.",
    why:
      "A weak or generic plan is the number one reason entrepreneur visa applications are delayed or refused. A credible, Portugal-specific plan builds trust with every authority you meet.",
    who: "D2 visa applicants, founders raising capital, businesses seeking bank support.",
    how: [
      "Company and value-proposition definition",
      "Portugal-specific market research",
      "3-year financial projections and cash-flow modelling",
      "Job creation and economic impact narrative",
      "Immigration-ready formatting and translation",
    ],
    duration: "2–3 weeks",
    cost: "From €900",
    faqs: [
      { q: "Is a business plan mandatory for the D2 visa?", a: "Yes. A detailed, viable business plan is a core requirement for the D2 entrepreneur visa." },
      { q: "Will you use real market data?", a: "Yes. Every plan uses current Portuguese market research, not generic templates." },
    ],
    related: ["company-formation", "business-consulting", "d2-visa"],
  },
  {
    slug: "nif-registration",
    group: "BUILD",
    icon: "Fingerprint",
    title: "NIF Registration",
    tagline: "Your Portuguese identity begins here.",
    summary:
      "Fast, remote registration of your Portuguese tax number (NIF) — the first practical step for banking, contracts, and every service in Portugal.",
    what:
      "The NIF (Número de Identificação Fiscal) is your Portuguese tax identification number. Nothing meaningful happens in Portugal — banking, renting, buying, incorporating — without it.",
    why:
      "The NIF is your gateway document. As a non-EU resident you also need a fiscal representative, which Effizent Europe provides, so you can obtain your NIF without being in Portugal.",
    who: "Anyone planning to relocate, invest, or do business in Portugal.",
    how: [
      "Document collection and verification",
      "Appointment of a compliant fiscal representative",
      "Submission to the Portuguese tax authority (Finanças)",
      "Delivery of your NIF, fully remote",
    ],
    duration: "3–10 days",
    cost: "From €150",
    faqs: [
      { q: "Can I get a NIF without visiting Portugal?", a: "Yes. With a power of attorney and a fiscal representative, Effizent Europe obtains your NIF entirely remotely." },
      { q: "Do I need a NIF before a visa?", a: "It is not legally required before the visa, but it is essential for opening a bank account and forming a company, so we recommend it early." },
    ],
    related: ["bank-account", "company-formation", "documentation"],
  },
  {
    slug: "bank-account",
    group: "BUILD",
    icon: "Landmark",
    title: "Banking",
    tagline: "A Portuguese bank account, opened with confidence.",
    summary:
      "Guided opening of a Portuguese business and personal bank account, coordinating with the bank so your capital, salary and visa proof are handled correctly.",
    what:
      "A Portuguese bank account lets you deposit share capital, receive income, pay taxes and demonstrate financial means for your visa.",
    why:
      "Non-residents often face rejections and paperwork loops when approaching Portuguese banks alone. We prepare and coordinate the application so it is accepted the first time.",
    who: "Company founders, visa applicants and new residents.",
    how: [
      "Bank selection based on your profile",
      "Preparation of KYC and source-of-funds documents",
      "Coordination and appointment scheduling",
      "Support with initial deposit and online banking setup",
    ],
    duration: "1–3 weeks",
    cost: "From €250",
    faqs: [
      { q: "Can I open an account before moving?", a: "Yes. With your NIF and passport we can help open an account remotely or during a short visit." },
    ],
    related: ["nif-registration", "company-formation", "accounting"],
  },
  // MOVE
  {
    slug: "d2-visa",
    group: "MOVE",
    icon: "Stamp",
    title: "Visa",
    tagline: "The right visa for your journey — done properly.",
    summary:
      "Complete visa strategy and application support across Portugal's residence routes: D2 entrepreneur, D4 student, D3 skilled worker, digital nomad and family.",
    what:
      "A Portuguese residence visa allows you to live in Portugal legally. The route depends on your goal — building a business (D2), studying (D4), working (D3) or joining family.",
    why:
      "The visa is only one chapter of your journey, but it must be handled precisely. Effizent Europe aligns your visa with your business, banking and settlement plans so nothing contradicts.",
    who: "Entrepreneurs, students, skilled workers and families.",
    how: [
      "Eligibility assessment and route selection",
      "Document checklist and preparation",
      "Business plan and financial evidence alignment",
      "Consulate appointment and submission support",
      "Post-approval residence (AIMA) coordination",
    ],
    duration: "2–4 months (route dependent)",
    cost: "From €1,200",
    faqs: [
      { q: "What is the Portugal D2 visa?", a: "The Portugal D2 visa is an entrepreneur/residence visa that lets non-EU citizens establish or operate a business in Portugal and live there legally." },
      { q: "How much money do I need?", a: "Requirements vary by route, but you must show savings equal to at least 12 months at Portuguese minimum wage plus your business investment. We model this precisely for you." },
    ],
    related: ["documentation", "business-plan", "company-formation", "travel"],
  },
  {
    slug: "documentation",
    group: "MOVE",
    icon: "FileCheck",
    title: "Documentation",
    tagline: "Every document, apostilled, translated and ready.",
    summary:
      "We assemble, apostille, translate and organise every document your visa and residence process requires — so nothing is missing on the day it matters.",
    what:
      "Documentation is the collection of certified papers — criminal records, proof of means, accommodation, insurance and more — required by Portuguese authorities.",
    why:
      "A single missing apostille or expired certificate can restart your whole process. Meticulous document management is where relocations are won or lost.",
    who: "Every visa and residence applicant.",
    how: [
      "Personalised document checklist",
      "Apostille and legalisation coordination",
      "Certified Portuguese translation",
      "Insurance and accommodation proof",
      "Final review before submission",
    ],
    duration: "2–5 weeks",
    cost: "From €400",
    faqs: [
      { q: "Do documents need to be in Portuguese?", a: "Yes, most official documents require certified Portuguese translation and an apostille. We manage both." },
    ],
    related: ["d2-visa", "nif-registration", "arrival-support"],
  },
  {
    slug: "travel",
    group: "MOVE",
    icon: "Plane",
    title: "Travel",
    tagline: "From India to Portugal, planned to the last detail.",
    summary:
      "Flight planning, timing and pre-departure coordination so your move aligns perfectly with your visa dates, company setup and arrival support.",
    what:
      "Travel planning covers the timing and logistics of your physical move — flights, routing, luggage, and synchronising arrival with your accommodation and registration appointments.",
    why:
      "Arriving before your NIF, bank or AIMA appointment is ready wastes precious visa time. We sequence your move so every day in Portugal counts.",
    who: "Individuals and families relocating from India.",
    how: [
      "Optimal travel-date planning around your visa",
      "Flight and routing guidance",
      "Pre-departure and packing checklist",
      "Arrival scheduling with ground support",
    ],
    duration: "Ongoing",
    cost: "Included in relocation packages",
    faqs: [
      { q: "Do you book flights?", a: "We plan and advise on optimal timing and routing; booking can be handled by you or our travel partners." },
    ],
    related: ["accommodation", "arrival-support", "d2-visa"],
  },
  {
    slug: "accommodation",
    group: "MOVE",
    icon: "Home",
    title: "Accommodation",
    tagline: "A home waiting for you when you land.",
    summary:
      "Help finding and securing accommodation in the right neighbourhood — verified listings, lease review and the address proof your residence process needs.",
    what:
      "Accommodation support covers finding, vetting and securing a place to live, plus providing the proof of address required for your residence permit.",
    why:
      "The rental market in Lisbon and Porto is competitive and remote scams are common. Local, verified help protects your money and secures a legitimate address for AIMA.",
    who: "New arrivals, families and remote founders.",
    how: [
      "Neighbourhood matching to your lifestyle and budget",
      "Verified listings and viewings (in person or virtual)",
      "Lease negotiation and contract review",
      "Address proof for residence registration",
    ],
    duration: "1–4 weeks",
    cost: "From €500",
    faqs: [
      { q: "Can you help before I arrive?", a: "Yes. We can shortlist and even secure accommodation before you land so you have a home from day one." },
    ],
    related: ["arrival-support", "travel", "lisbon"],
  },
  {
    slug: "arrival-support",
    group: "MOVE",
    icon: "MapPin",
    title: "Arrival Support",
    tagline: "The ground team for your first days.",
    summary:
      "Airport pickup, SIM card, local registration, AIMA appointments and a real person on the ground to make your first weeks effortless.",
    what:
      "Arrival support is hands-on, in-person assistance for the practical tasks of your first days: getting connected, registered and settled.",
    why:
      "The first two weeks decide how smoothly your new life begins. Local support removes the confusion, the queues and the language barrier.",
    who: "Every new arrival and their family.",
    how: [
      "Airport pickup and orientation",
      "Portuguese SIM card and connectivity",
      "Social security (NISS) and local registration",
      "AIMA / residence appointment accompaniment",
      "Local orientation: transport, healthcare, essentials",
    ],
    duration: "First 2–4 weeks",
    cost: "From €600",
    faqs: [
      { q: "Will someone meet me in person?", a: "Yes. Our ground team meets you and accompanies you through your key first-week tasks." },
    ],
    related: ["accommodation", "accounting", "documentation"],
  },
  // GROW
  {
    slug: "accounting",
    group: "GROW",
    icon: "Calculator",
    title: "Accounting",
    tagline: "Books that keep your business compliant and clear.",
    summary:
      "Certified accounting (contabilista certificado) for your Portuguese company — monthly bookkeeping, payroll, VAT and the reporting the law requires.",
    what:
      "Portuguese companies are legally required to have a certified accountant. Accounting covers bookkeeping, payroll, VAT returns and statutory filings.",
    why:
      "Compliance in Portugal is strict and deadlines are unforgiving. A dedicated certified accountant keeps you penalty-free and gives you a clear financial picture.",
    who: "Every registered Portuguese company.",
    how: [
      "Monthly bookkeeping and reconciliation",
      "Certified accountant (TOC) representation",
      "VAT (IVA) returns and payroll processing",
      "Annual accounts and statutory filing",
    ],
    duration: "Ongoing monthly",
    cost: "From €150 / month",
    faqs: [
      { q: "Is a certified accountant mandatory?", a: "Yes. Portuguese law requires companies to have a certified accountant (contabilista certificado)." },
    ],
    related: ["tax", "company-formation", "hiring"],
  },
  {
    slug: "tax",
    group: "GROW",
    icon: "Receipt",
    title: "Tax",
    tagline: "Pay what you owe — not a euro more.",
    summary:
      "Corporate and personal tax planning, IVA, and guidance on Portugal's tax incentives so your structure is efficient and fully compliant.",
    what:
      "Tax support covers corporate income tax (IRC), personal income tax (IRS), VAT (IVA) and planning around Portugal's available incentives and treaties.",
    why:
      "Portugal has a double-taxation treaty with India and specific incentive regimes. Proper planning legally reduces your burden and prevents surprises.",
    who: "Company owners and new tax residents.",
    how: [
      "Corporate and personal tax planning",
      "IVA and IRC/IRS compliance",
      "Double-taxation treaty guidance (India–Portugal)",
      "Incentive and residency-regime advice",
    ],
    duration: "Ongoing",
    cost: "From €200 / quarter",
    faqs: [
      { q: "Will I be taxed in both India and Portugal?", a: "Portugal and India have a double-taxation avoidance agreement. We structure your affairs so income is not taxed twice." },
    ],
    related: ["accounting", "expansion", "company-formation"],
  },
  {
    slug: "hiring",
    group: "GROW",
    icon: "Users",
    title: "Hiring",
    tagline: "Build your team the Portuguese way.",
    summary:
      "Recruitment, compliant employment contracts, payroll and HR setup so you can hire in Portugal correctly and confidently.",
    what:
      "Hiring support covers recruitment, Portuguese employment contracts, social security registration and payroll for your first and future employees.",
    why:
      "Portuguese labour law is employee-protective and specific. Getting contracts and payroll right protects your business and your reputation as an employer.",
    who: "Growing companies ready to build a team.",
    how: [
      "Recruitment and candidate sourcing",
      "Compliant employment contracts",
      "Social security and payroll registration",
      "HR policy and onboarding setup",
    ],
    duration: "Ongoing",
    cost: "From €300 per hire",
    faqs: [
      { q: "Can my company sponsor foreign employees?", a: "Yes. A Portuguese company can sponsor work visas for skilled talent; we manage the process end to end." },
    ],
    related: ["accounting", "tax", "expansion"],
  },
  {
    slug: "expansion",
    group: "GROW",
    icon: "TrendingUp",
    title: "Expansion",
    tagline: "Portugal today, Europe tomorrow.",
    summary:
      "Strategy and execution for scaling beyond Portugal into the wider European Union — new markets, entities, funding and partnerships.",
    what:
      "Expansion support helps your established Portuguese business grow across the EU: new market entry, additional entities, EU funding and strategic partnerships.",
    why:
      "Portugal is your launchpad, not your ceiling. With an EU base, the entire single market opens — and Effizent Europe helps you reach it.",
    who: "Profitable businesses ready to scale internationally.",
    how: [
      "EU market-entry strategy",
      "Cross-border entity and tax structuring",
      "EU grant and funding guidance",
      "Partner and distributor sourcing",
    ],
    duration: "Ongoing",
    cost: "Custom engagement",
    faqs: [
      { q: "Can I use my Portuguese company to trade across the EU?", a: "Yes. A Portuguese company can trade freely across the EU single market and register for cross-border VAT (VIES)." },
    ],
    related: ["tax", "business-consulting", "hiring"],
  },
];

export const SERVICE_GROUPS = {
  BUILD: {
    label: "Build",
    blurb: "Lay the foundation — your company, identity and financial base in Portugal.",
    slugs: ["company-formation", "business-consulting", "business-plan", "nif-registration", "bank-account"],
  },
  MOVE: {
    label: "Move",
    blurb: "Make the journey — visa, documents, travel and a soft landing.",
    slugs: ["d2-visa", "documentation", "travel", "accommodation", "arrival-support"],
  },
  GROW: {
    label: "Grow",
    blurb: "Build your life — run, comply, hire and expand across Europe.",
    slugs: ["accounting", "tax", "hiring", "expansion"],
  },
};

// The 10-milestone Portugal Success Roadmap (homepage cinematic scroll)
export const MILESTONES = [
  { n: "01", title: "Discover Your Opportunity", theme: "What can Portugal become for you?", body: "Before a single document is signed, we help you see the picture clearly — the market, the lifestyle, the growth. Portugal is not a destination. It is a decision.", icon: "Compass" },
  { n: "02", title: "Build Your Foundation", theme: "Company formation, planning, strategy.", body: "A blueprint becomes a company. We structure your business, write the plan that convinces banks and immigration, and set your financial strategy.", icon: "Building2" },
  { n: "03", title: "Establish Your Identity", theme: "Your Portuguese NIF.", body: "From particles of paperwork, your Portuguese identity forms. The NIF is the key that unlocks everything that follows.", icon: "Fingerprint" },
  { n: "04", title: "Build Your Financial Base", theme: "A Portuguese bank account.", body: "Your financial ecosystem takes shape — capital deposited, means proven, ready to move and to trade.", icon: "Landmark" },
  { n: "05", title: "Prepare Your Move", theme: "Documentation and visa.", body: "Documents assemble elegantly. Your visa is one important chapter — handled with precision, never allowed to overwhelm the story.", icon: "FileCheck" },
  { n: "06", title: "Your Journey To Portugal", theme: "Flight planning.", body: "The aircraft continues its journey. We plan your move so every day of your new life begins exactly on time.", icon: "Plane" },
  { n: "07", title: "Your New Home", theme: "Accommodation.", body: "A Portuguese neighbourhood builds itself around you — a verified home, a real address, a place to belong.", icon: "Home" },
  { n: "08", title: "Landing Support", theme: "Ground assistance.", body: "The city map activates. Airport pickup, SIM card, registration — a real team on the ground for your first days.", icon: "MapPin" },
  { n: "09", title: "Building Your Life", theme: "Accounting, tax, hiring, growth.", body: "A small business grows into an ecosystem. Compliant, staffed, profitable — your future taking root.", icon: "TrendingUp" },
  { n: "10", title: "Growing Beyond Portugal", theme: "European expansion.", body: "Portugal expands into Europe. Your base becomes a launchpad into the entire single market.", icon: "Globe" },
];

export const TESTIMONIALS = [
  { name: "Rohan & Meera Kapoor", role: "Founders, tech services · Lisbon", quote: "We arrived with an idea and a lot of fear. Effizent turned it into a registered company, a D2 visa and a home in Lisbon. They were with us at every appointment.", initials: "RK" },
  { name: "Aditya Sharma", role: "Import–export · Porto", quote: "The business plan they built passed the consulate without a single question. That document alone was worth everything.", initials: "AS" },
  { name: "The Nair Family", role: "Relocated from Kochi · Braga", quote: "Airport pickup, SIM cards, school guidance, the NISS queue — they handled the parts that would have broken us. We just lived our new life.", initials: "NF" },
];

export const TEAM = [
  { name: "Founder's Story", role: "Why Effizent Europe exists", note: "Born from the journey of Indians who built their own future in Portugal — and decided no one should have to do it alone." },
];

export const STATS = [
  { value: "500+", label: "Journeys guided" },
  { value: "7", label: "Portuguese cities" },
  { value: "10", label: "Milestone roadmap" },
  { value: "1", label: "Partner, every step" },
];

export const INTEREST_OPTIONS = [
  "Company Formation",
  "Business Consulting",
  "D2 Entrepreneur Visa",
  "Student Visa (D4)",
  "NIF & Banking",
  "Relocation & Settlement",
  "Accounting & Tax",
  "Not sure yet",
];

export const TIMELINE_OPTIONS = ["Immediately", "1–3 months", "3–6 months", "6–12 months", "Just exploring"];
export const BUDGET_OPTIONS = ["Under €5,000", "€5,000 – €15,000", "€15,000 – €50,000", "€50,000+", "Prefer not to say"];
export const JOURNEY_OPTIONS = ["Entrepreneur / Business", "Student", "Professional / Worker", "Family", "Investor"];
